import { normalizeNationalProjectsSnapshot, type NationalProjectsSnapshot } from './nationalProjects';

/** Local design fixture. The page enables it only in development and labels it NOT LIVE. */
export function createNationalProjectsSample(): NationalProjectsSnapshot {
  return normalizeNationalProjectsSnapshot({
    available: true,
    generated_at: '2026-09-15T11:42:00Z',
    coverage: {
      project_count: 3,
      linked_application_count: 58,
      source_backed_link_count: 49,
      inferred_link_count: 9,
      councils_represented: 11,
      councils_expected: 32,
      projects_with_complete_evidence: 1,
    },
    projects: [
      {
        project_key: 'sample-transmission-north',
        slug: 'transmission-northbound-build-out',
        title: 'The transmission northbound build-out',
        summary: 'Substations, overhead lines and storage proposals reveal the physical footprint of Scotland’s electricity-grid expansion.',
        status: 'Consultation & applications',
        priority: 'National',
        category: 'Energy infrastructure',
        confidence: 0.91,
        evidence_kind: 'mixed',
        evidence_summary: '24 direct planning records and four inferred project relationships still awaiting source confirmation.',
        source_state: 'current',
        review_state: 'approved',
        labels: { summary: 'agent_inference', claims: 'source_bound_synthesis', applications: 'official_records' },
        councils: [
          { name: 'Highland', application_count: 8 },
          { name: 'Aberdeenshire', application_count: 7 },
          { name: 'Perth & Kinross', application_count: 5 },
          { name: 'Angus', application_count: 4 },
          { name: 'Moray', application_count: 4 },
        ],
        application_count: 28,
        evidence_count: 24,
        inference_count: 4,
        first_seen_at: '2025-11-14',
        updated_at: '2026-09-15',
        tags: ['Grid', 'Battery storage', 'Landscape'],
        applications: [
          { application_uid: 'sample-abd-1', reference_no: 'APP/2026/1842', title: 'Battery storage and grid connection', council: 'Aberdeenshire', address: 'Land south of New Deer substation', status: 'Awaiting assessment', relationship: 'source', source_hash: 'sample:bc129e', source_url: '/planning-updates' },
          { application_uid: 'sample-hig-1', reference_no: '24/04122/S36', title: 'New 400kV overhead line section', council: 'Highland', address: 'Beauly to Spittal corridor', status: 'Consultation', relationship: 'source', source_hash: 'sample:47f813', source_url: '/planning-updates' },
          { application_uid: 'sample-mor-1', reference_no: 'LINK-07', title: 'Potential enabling works relationship', council: 'Moray', address: 'Keith area', status: 'Needs confirmation', relationship: 'inference', source_url: null },
        ],
        sources: [
          { source_key: 'sample-register-abd', label: 'Aberdeenshire planning register', publisher: 'Aberdeenshire Council', url: '/planning-updates', checked_at: '2026-09-15', evidence_kind: 'source' },
          { source_key: 'sample-register-hig', label: 'Highland planning register', publisher: 'The Highland Council', url: '/planning-updates', checked_at: '2026-09-15', evidence_kind: 'source' },
        ],
        timeline: [
          { event_key: 'sample-grid-3', at: '2026-09-15', title: 'Four linked applications added', detail: 'New storage and substation records expanded the project to five council areas.', evidence_kind: 'source', source_url: '/planning-updates' },
          { event_key: 'sample-grid-2', at: '2026-08-28', title: 'Moray relationship held as inference', detail: 'Location and promoter overlap suggest a link, but no primary project document confirms it yet.', evidence_kind: 'inference' },
          { event_key: 'sample-grid-1', at: '2025-11-14', title: 'Project first assembled', detail: 'CityScope linked the first eight source-backed applications.', evidence_kind: 'source', source_url: '/planning-updates' },
        ],
      },
      {
        project_key: 'sample-waterfronts',
        title: 'Scotland’s working waterfronts',
        summary: 'Port, fabrication and logistics applications chart an east-coast bet on the offshore-wind supply chain.',
        status: 'In delivery',
        priority: 'High',
        category: 'Industrial transition',
        confidence: 0.84,
        evidence_kind: 'source',
        evidence_summary: 'All 17 project links are grounded in planning records or named project documents.',
        source_state: 'current',
        review_state: 'approved',
        labels: { summary: 'agent_inference', claims: 'source_bound_synthesis', applications: 'official_records' },
        councils: [
          { name: 'Fife', application_count: 8 },
          { name: 'Dundee City', application_count: 5 },
          { name: 'Aberdeen City', application_count: 4 },
        ],
        application_count: 17,
        evidence_count: 17,
        inference_count: 0,
        first_seen_at: '2025-06-03',
        updated_at: '2026-09-10',
        tags: ['Ports', 'Offshore wind', 'Manufacturing'],
        applications: [
          { application_uid: 'sample-fif-1', reference_no: '26/01451/EIA', title: 'Fabrication hall and quayside works', council: 'Fife', address: 'Energy Park Fife', status: 'Pending', relationship: 'source', source_hash: 'sample:49b2e8', source_url: '/planning-updates' },
        ],
        sources: [{ source_key: 'sample-fife-register', label: 'Fife planning register', publisher: 'Fife Council', url: '/planning-updates', checked_at: '2026-09-10', evidence_kind: 'source' }],
        timeline: [
          { event_key: 'sample-port-2', at: '2026-09-10', title: 'Fabrication hall application received', detail: 'A new source-backed application was added to the Fife cluster.', evidence_kind: 'source', source_url: '/planning-updates' },
          { event_key: 'sample-port-1', at: '2025-06-03', title: 'Project first tracked', detail: 'Port and logistics records were assembled across three councils.', evidence_kind: 'source', source_url: '/planning-updates' },
        ],
      },
      {
        project_key: 'sample-station-districts',
        title: 'Station districts after the offices era',
        summary: 'Housing conversions and retrofit proposals are gathering around major rail stations in the Central Belt.',
        status: 'Evidence review',
        priority: 'Watch',
        category: 'Urban regeneration',
        confidence: 0.68,
        evidence_kind: 'mixed',
        evidence_summary: 'Nine direct applications are linked; four place-level relationships remain inference.',
        source_state: 'current',
        review_state: 'approved',
        labels: { summary: 'agent_inference', claims: 'source_bound_synthesis', applications: 'official_records' },
        councils: [
          { name: 'Glasgow City', application_count: 6 },
          { name: 'Edinburgh', application_count: 4 },
          { name: 'South Lanarkshire', application_count: 3 },
        ],
        application_count: 13,
        evidence_count: 8,
        inference_count: 5,
        first_seen_at: '2026-03-19',
        updated_at: '2026-09-08',
        tags: ['Housing', 'Adaptive reuse', 'Rail'],
        applications: [],
        sources: [],
        timeline: [{ event_key: 'sample-station-1', at: '2026-09-08', title: 'Evidence review opened', detail: 'Four potential links need a primary document or explicit application relationship.', evidence_kind: 'inference' }],
      },
    ],
  });
}
